import Pagination from "@mui/material/Pagination";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useLeaseCompStore, usePopupStore, useViewSettingsStore } from "../..";
import useActivePage from "../../hooks/useActivePage";
import useLeaseComps, { LeaseCompsSortingType } from "../../hooks/useLeaseComps";
import useLeaseCompsOwnerFilterValues from "../../hooks/useLeaseCompsOwnerFilterValues";
import useLeaseCompsWithFocusArea from "../../hooks/useLeaseCompsWithFocusArea";
import useLeaseCompsWithGlobalFilters from "../../hooks/useLeaseCompsWithGlobalFilters";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import usePagination from "../../hooks/usePagination";
import PopupType from "../../interfaces/PopupType";
import { GlobalFiltersTab } from "../../store/FilterSettingsStore";
import { formatNumber } from "../../utils/utils";
import GlobalFiltersIcon from "../icons/GlobalFiltersIcon";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import LeaseCompInfo from "./LeaseCompInfo";

function LeaseComps() {
    const { id } = useParams();
    const isLeaseCompPage = useMatch("/leaseComps/*");
    const leaseCompStore = useLeaseCompStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const navigateToLeaseComp = useNavigateToLeaseComp();
    const ownerFilterValues = useLeaseCompsOwnerFilterValues();
    const [ownerFilter, setOwnerFilter] = useState(ownerFilterValues[0]);
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(LeaseCompsSortingType.ModifiedNewest);
    const [page, setPage] = useState(1);
    const leaseComps = useLeaseCompsWithFocusArea(useLeaseCompsWithGlobalFilters(useLeaseComps(ownerFilter, debouncedSearchFilter, sortBy)));
    const [pageCount, pagedLeaseComps] = usePagination(leaseComps, page, () => setPage(1));
    useActivePage(leaseComps, Number(id), isLeaseCompPage && id && leaseComps.find(lc => lc.id === Number(id)), searchFilter, ownerFilter, sortBy, setPage);

    useEffect(() => {
        if (!ownerFilterValues.includes(ownerFilter))
            setOwnerFilter(ownerFilterValues[0]);
    }, [ownerFilterValues, ownerFilter]);

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!leaseCompStore.isLeaseCompsLoading.fetching && formatNumber(leaseComps.length)} Lease Comps
                {!viewSettingsStore.isMobile && <GlobalFiltersIcon tab={GlobalFiltersTab.LeaseComps} className={tabStyles.headerButton} />}
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus={!viewSettingsStore.isMobile} placeholder="Search lease comps.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
                        {ownerFilterValues.map(v => <option key={v}>{v}</option>)}
                    </select>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={LeaseCompsSortingType.ModifiedNewest}>Modified (Newest)</option>
                        <option value={LeaseCompsSortingType.ModifiedOldest}>Modified (Oldest)</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {leaseCompStore.isLeaseCompsLoading.fetching ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedLeaseComps.map(lc => (
                        <LeaseCompInfo
                            key={lc.id}
                            className={[
                                tabStyles.item,
                                isLeaseCompPage && id === lc.id.toString()
                                    ? tabStyles.opened
                                    : popupStore.type === PopupType.LeaseComp && popupStore.id === lc.id
                                        ? tabStyles.selected
                                        : "",
                            ].join(" ")}
                            pipedriveCompanyId={lc.pipedriveCompanyId}
                            address={lc.address}
                            totalRent={lc.totalRent}
                            parcelSizeAcres={lc.parcelSizeAcres}
                            buildingSize={lc.buildingSize}
                            onClick={() => navigateToLeaseComp(lc.id)}
                        />
                    ))
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    )
}

export default observer(LeaseComps);